import React, { useEffect, useState, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Card, CardHeader, CardBody, CardTitle, Row, Col, Badge, Spinner } from 'reactstrap';
import Chart from 'react-apexcharts';
import { Users, FileText } from 'react-feather';
import './assets/home.scss';
import { getHomeData, homeDataSelector } from '../../redux/auth';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';

const MySwal = withReactContent(Swal);

const Home = () => {
  const dispatch = useDispatch();
  const userData = useSelector((state) => state.auth.login.loggedInUser);
  const { data, loading, error } = useSelector(homeDataSelector);
  const checked = localStorage.getItem('istNetworkChecked');
  const [istNetworkChecked, setIstNetworkChecked] = useState(
    localStorage.getItem('istNetworkChecked') === 'true' ? true : false
  );
  const toastShownRef = useRef(false);

  // Load data on component mount
  useEffect(() => {
    dispatch(getHomeData()).then(() => {
      // Check localStorage only once during initial mount
      if (checked) {
        setIstNetworkChecked(true);
      }
    });
  }, [dispatch]);

  // Check for IST Networks vendor and show toast if not found
  useEffect(() => {
    // Ensure this runs only once when data is available
    if (data?.vendors && !istNetworkChecked && !toastShownRef.current) {
      const hasIstNetworks = data.vendors.some(
        (vendor) =>
          vendor.vendor.toLowerCase() === 'istnetworks' ||
          vendor.vendor.toLowerCase() === 'ist networks'
      );

      if (!hasIstNetworks) {
        MySwal.fire({
          title: 'Suggestion',
          text: 'Would you like to try the IST Networks vendor? Contact your administrator for more information.',
          icon: 'info',
          // toast: true,
          // position: 'top-end',
          showConfirmButton: true,
          // timer: 5000,
          timerProgressBar: true
        });

        // Save to localStorage to prevent showing in future sessions
        localStorage.setItem('istNetworkChecked', 'true');
        setIstNetworkChecked(true);
      }

      // Mark that we've checked for toast in this session
      toastShownRef.current = true;
    }
  }, [data, istNetworkChecked]);

  // Character usage statistics from API - with validation to prevent NaN values
  const totalCharacters = data?.original_number_of_chars || 0;
  const usedCharacters =
    data?.original_number_of_chars && data?.number_of_chars
      ? data.original_number_of_chars - data.number_of_chars
      : 0;
  const remainingCharacters = totalCharacters - usedCharacters;
  const usagePercentage =
    totalCharacters > 0 ? Math.round((usedCharacters / totalCharacters) * 100) : 0;

  // Vendor data from API
  const vendorData = data?.vendors || [];

  // Chart options for character usage
  const donutOptions = {
    chart: {
      type: 'donut'
    },
    colors: ['#28c76f', '#e9ecef'],
    labels: ['Used', 'Remaining'],
    plotOptions: {
      pie: {
        donut: {
          labels: {
            show: true,
            name: {
              show: true,
              fontSize: '22px',
              fontFamily: 'Montserrat'
            },
            value: {
              show: true,
              fontSize: '16px',
              fontFamily: 'Montserrat',
              formatter: (val) => `${val.toLocaleString()}`
            },
            total: {
              show: true,
              label: 'Total',
              formatter: () => `${totalCharacters.toLocaleString()}`
            }
          }
        }
      }
    },
    legend: {
      show: true,
      position: 'bottom'
    },
    responsive: [
      {
        breakpoint: 480,
        options: {
          chart: {
            height: 300
          },
          legend: {
            position: 'bottom'
          }
        }
      }
    ]
  };

  // Chart series data - ensure valid numbers
  const donutSeries = [usedCharacters || 0, remainingCharacters || 0];

  if (loading) {
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ minHeight: '70vh' }}>
        <Spinner color="primary" />
      </div>
    );
  }

  return (
    <div className="home-container">
      <h1 className="mb-2">Welcome, {userData?.username || 'User'}</h1>
      <p className="mb-4">Here's an overview of your Nūn AI voice services</p>

      {error && (
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
      )}

      <Row className="match-height">
        {/* Character Usage Statistics */}
        <Col lg="4" md="6" sm="12">
          <Card className="character-stats-card">
            <CardHeader className="d-flex justify-content-between align-items-center">
              <CardTitle tag="h4">Character Usage</CardTitle>
              <FileText size={20} />
            </CardHeader>
            <CardBody>
              <div className="d-flex justify-content-between mb-2">
                <div className="d-flex flex-column">
                  <span className="font-weight-bold">{remainingCharacters.toLocaleString()}</span>
                  <span className="text-muted">Characters Remaining</span>
                </div>
                <div className="d-flex flex-column text-right">
                  <span className="font-weight-bold">{usedCharacters.toLocaleString()}</span>
                  <span className="text-muted">Characters Used</span>
                </div>
              </div>
              <div className="progress-wrapper">
                <div className="progress-label d-flex justify-content-between">
                  <span>{usagePercentage}% Used</span>
                  <span>{100 - usagePercentage}% Remaining</span>
                </div>
                <div className="progress">
                  <div
                    className="progress-bar"
                    role="progressbar"
                    style={{ width: `${usagePercentage}%` }}
                    aria-valuenow={usagePercentage}
                    aria-valuemin="0"
                    aria-valuemax="100"></div>
                </div>
              </div>
              {data?.expiration_date && (
                <div className="d-flex flex-column text-right">
                  <span className="text-muted">Expires on: {data.expiration_date}</span>
                </div>
              )}
            </CardBody>
          </Card>
        </Col>

        {/* Character Usage Chart */}
        <Col lg="4" md="6" sm="12">
          <Card className="character-chart-card">
            <CardHeader className="d-flex justify-content-between align-items-center">
              <CardTitle tag="h4">Character Distribution</CardTitle>
            </CardHeader>
            <CardBody>
              <Chart options={donutOptions} series={donutSeries} type="donut" height={300} />
            </CardBody>
          </Card>
        </Col>

        {/* Vendor Information */}
        {/* <Col lg="4" md="12" sm="12">
          <Card className="vendor-card">
            <CardHeader className="d-flex justify-content-between align-items-center">
              <CardTitle tag="h4">Available Vendors</CardTitle>
              <Users size={20} />
            </CardHeader>
            <CardBody>
              <div className="vendors-list">
                {vendorData.map((vendor, index) => (
                  <div
                    key={index}
                    className="vendor-item d-flex justify-content-between align-items-center mb-2">
                    <div className="vendor-info">
                      <h5 className="mb-0">{vendor.vendor}</h5>
                      <small className="text-muted">{vendor.voices} available voices</small>
                    </div>
                    <Badge color={vendor.status === 'active' ? 'light-success' : 'light-secondary'}>
                      {vendor.status}
                    </Badge>
                  </div>
                ))}
              </div>
              <div className="mt-3">
                <h6 className="text-primary">
                  Total Available Voices:{' '}
                  {vendorData.reduce((sum, vendor) => sum + vendor.voices, 0)}
                </h6>
              </div>
            </CardBody>
          </Card>
        </Col> */}
      </Row>

      {data?.additional_stats && (
        <Row className="match-height mt-2">
          <Col lg="12">
            <Card>
              <CardHeader>
                <CardTitle tag="h4">Additional Statistics</CardTitle>
              </CardHeader>
              <CardBody>
                <div className="d-flex flex-wrap justify-content-between">
                  {Object.entries(data.additional_stats).map(([key, value], index) => (
                    <div key={index} className="stat-item text-center p-2">
                      <h5>{key.replace(/_/g, ' ')}</h5>
                      <h3>{value}</h3>
                    </div>
                  ))}
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>
      )}
    </div>
  );
};

export default Home;
